// price-reveal-1 — "Receipt Build" (organic, v1 port)
//
// The price-transparency format: the unGoogleable money question up top, then a
// receipt builds one line item per beat (rows PERSIST and stack, dotted leaders
// for the till feel), and the total stamps down big on the final beat with an
// optional worth-comparison line. Withholding the total is the retention
// engine, so the total block is anchored to the END of the master.
//
// Renderdoc port of the v1 PriceRevealLayer
// (packages/remotion/src/components/price-reveal-layer.tsx), built from generic
// blocks only:
//   - spine: procedure b-roll, full-bleed, looping, beat-synced cuts every 4
//     beats (template def `price-reveal.beatsPerEdit: 4`).
//   - spine: a `solid` carrying v1's 4-stop vertical scrim gradient verbatim
//     (the `solid` colour field accepts any CSS background string).
//   - overlay: the hook, twice — a big centred beat for the first segment, then
//     a small top strap for the rest (delayed entrance), reproducing v1's
//     `hookShrunk` state change.
//   - overlay: an ACCUMULATING `staggered-list` for the receipt rows, one per
//     4-beat window, starting after the hook beat.
//   - overlay: an `info-card` for the total (label + accent-filled pill) plus a
//     separate `text` for the optional value line, both `start: 'from-end'`.
//
// ── Content contract ────────────────────────────────────────────────
// v1 draftConfig block is `priceReveal` (organicCopyToConfigBlock, case
// 'price-reveal'): { hook, items: {name, price}[], totalPrice, valueLine? }.
// v2 reaches copy only through the five `script-text` roles in slot.ts
// (hook | body | cta | disclaimer | list), so:
//
//   v1 field                 → v2 slot                       → block
//   ───────────────────────────────────────────────────────────────────
//   hook                     → script-text 'hook'            → hook-lead +
//                                                              hook-strap
//   items[] (name + price)   → script-text 'body' (array)    → receipt list
//   totalPrice               → script-text 'cta'             → total-card.cta
//   valueLine (optional)     → script-text 'disclaimer'      → value-line text
//   primaryColor             → theme accent (info-card CTA fill)
//
// ── Why the total rides the info-card `cta`, not its `price` ────────
// `TemplateInfoCard.price` exists and looks like the natural home, but it does
// NOT fit here:
//   1. `discoverScriptSlots` / `fillScriptSlots` (synthesize-template/
//      script-parsing.ts) visit an info-card's headline / items / cta only —
//      never `price`. A query slot there would never be requested from the
//      script model, so the total would compile empty.
//   2. The renderer prints `{currency}{value}` (blocks/info-card.tsx), while
//      `priceRevealCopySchema` already emits a symbol-inclusive string ("price
//      ≤9 chars (currency symbol included)", generate-organic-copy/prompts.ts).
//      Feeding it through `price` would double the symbol ("€€120").
//   For the same reason theme `identity.currency` is deliberately NOT applied
//   here — unlike offer-square-1, whose price arrives as a bare number that the
//   compiler must prefix. If the copy contract ever changes to a numeric total,
//   move it onto `price` and bind `currency` the way offer-square-1 does.
//   The `cta` field is also the closest VISUAL match: the renderer fills it with
//   the theme accent and picks a contrasting label colour — exactly v1's
//   `backgroundColor: accent` + `accentTextColor(accent)` total pill.
//
// ── Timing ──────────────────────────────────────────────────────────
// v1 segments the runtime as hook + one per item + total, and the worker sizes
// the video as `(items.length + 2) * 2.2` seconds (apps/video-worker/src/
// main.ts). At the def's recommended 4 items that's 13.2 s = 396 frames, with a
// 2.2 s (66 frame) segment — so SEGMENT_FRAMES is both the hook beat and the
// total beat, and the receipt occupies everything between.
//
// FIDELITY GAPS vs v1 (v2 has no exit animations, no spring config, no nested
// containers, and no name/price row primitive):
//   - RECEIPT ROWS ARE ONE STRING. v1 lays each row out as [name] ····· [price]
//     with a 3px dotted leader, name 42/700 white and price 42/800 cream,
//     baseline-aligned across 84% width. v2's list item is a single text run, so
//     each row is whatever one line the script model produces ("Consultation —
//     €50"); there is no leader rule, no right-aligned price column and no
//     two-tone colouring.
//   - THE STAMP. v1 springs the total in with `scale 1.15 → 1` (an overshoot
//     that settles DOWN onto the frame, damping 13 / stiffness 170 / mass 0.8,
//     12 frames). No v2 token overshoots: `pop` is the closest — it scales
//     0.85 → 1 (spring damping 15 / stiffness 120 / mass 0.8), i.e. it grows
//     INTO place rather than stamping down. Kept at 12 frames to match the
//     window. `spring-in` (0.92 → 1) is the same shape, gentler.
//   - THE HOOK SHRINK. v1 re-renders one element from 60px centred to 34px at
//     the top strap — an instant swap at frame `per`. v2 has no per-block
//     re-layout, so this is two `text` blocks: a fixed-length centred one, and a
//     persistent strap whose `entranceDelayFrames` hides it until the swap. The
//     changeover is therefore a hard cut between two elements (visually the same
//     as v1's, which is also instantaneous), but for one frame both are laid out
//     — they occupy different bands (centre vs 7% top) so they don't collide.
//   - ROW PLATE. `container: 'plate'` is rgba(0,0,0,0.7) radius 16 vs v1's
//     rgba(13,15,24,0.62) radius 14, and it hugs its text instead of filling
//     84% of the width (v1 rows are full-width bars).
//   - TOTAL LABEL. v1's "Total" is 34px/800 uppercase, letterSpacing 0.24em,
//     rgba(255,255,255,0.85). The info-card headline takes a bare type-style
//     token (no `styleOverride` on headline/cta — see TemplateInfoCard), so it
//     renders at the `caption` default (24px uppercase, theme-owned tracking).
//   - TOTAL SIZE / FACE. v1 is 104px Inter 800. The `display` token is 96px and
//     defaults to Playfair (the theme may rebind the face), so the total reads
//     slightly smaller and, absent a theme override, serif.
//   - TOTAL PILL WIDTH. The info-card CTA is `alignSelf: 'stretch'`, so the pill
//     spans the card's placement width instead of hugging the number the way
//     v1's `padding: '10px 46px'` does. Placement width is pulled in to 0.62 to
//     approximate the hug.
//   - VALUE LINE POSITION. v1 stacks the value line inside the same flex column
//     as the total (gap 14, whole stack bottom-padded 14%). v2 can only reach it
//     through a `text` block (info-card `items` would draw checkmark markers),
//     so it is a sibling overlay pinned just below the card; the combined stack
//     therefore sits a little lower in the frame than v1's.
//   - ROW ENTRANCE. v1 springs each row in over 10 frames (damping 15 /
//     stiffness 150) with a 24px rise. `ListElement` carries no per-element
//     entrance duration, so rows use `slide-up` at the list default (12 frames).
//   - NO EXITS anywhere: v1 has none either on this layer (rows and the total
//     persist to the end), so this gap is nil in practice.
const FPS = 30;
// One v1 segment = 2.2 s (worker: `(items.length + 2) * 2.2` seconds across
// hook + items + total). This is the hook beat AND the total beat.
const SEGMENT_FRAMES = Math.round(2.2 * FPS); // 66
// Fallback length only (music-only template, no narration to drive it): the
// def's recommended 4 items → (4 + 2) segments.
const TOTAL_FRAMES = 6 * SEGMENT_FRAMES; // 396 = 13.2 s
// Everything between the hook beat and the end belongs to the receipt; the
// sequential overlay accumulator therefore starts it at SEGMENT_FRAMES.
const RECEIPT_FRAMES = TOTAL_FRAMES - SEGMENT_FRAMES;
// v1: the total springs in over durationInFrames: 12.
const STAMP_FRAMES = 12;
export const priceReveal1 = {
    id: 'price-reveal-1',
    schemaVersion: 2,
    aspectRatios: ['portrait'],
    duration: { kind: 'fixed', frames: TOTAL_FRAMES },
    root: {
        kind: 'leaf',
        id: 'main',
        spine: [
            // Treatment/clinic footage behind the receipt. Def: recommendedClipCount
            // 4, maxBRollClips 6, "one per receipt beat".
            {
                kind: 'media-track',
                id: 'broll',
                duration: { kind: 'fill' },
                clips: {
                    source: 'query',
                    query: { kind: 'asset-clips', tag: 'procedure', count: [1, 6] },
                    required: true,
                },
                fit: 'contain-blur',
                cuts: { mode: 'beat-synced', beatsPerEdit: 4 },
                fillMode: 'loop',
            },
            // v1's scrim, copied verbatim — dark at the hook strap and under the
            // total, lifted through the middle where the receipt rows carry their own
            // plates.
            {
                kind: 'solid',
                id: 'scrim',
                duration: { kind: 'fill' },
                color: {
                    source: 'fixed',
                    value: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.3) 34%, rgba(0,0,0,0.3) 62%, rgba(0,0,0,0.6) 100%)',
                },
            },
        ],
        overlays: [
            // Beat 1 — the question, big and centred (v1 `hookShrunk === false`:
            // 60px/800, centred, padding '0 8%', no entrance animation).
            {
                kind: 'text',
                id: 'hook-lead',
                text: {
                    source: 'query',
                    query: { kind: 'script-text', role: 'hook' },
                    required: true,
                },
                style: 'heading',
                styleOverride: {
                    fontSize: 60,
                    fontWeight: 800,
                    textTransform: 'none',
                    letterSpacing: 0,
                    color: '#FFFFFF',
                    textShadow: '0 3px 20px rgba(0,0,0,0.6)',
                    strokeWidth: 0,
                },
                animation: { entrance: 'none' },
                placement: { anchor: 'center', x: 0.5, y: 0.5, width: 0.84 },
                duration: { kind: 'fixed', frames: SEGMENT_FRAMES },
            },
            // Beats 2..N — the same question shrunk to a top strap (v1
            // `hookShrunk === true`: 34px/800, paddingTop 7%). Spans the master but
            // stays hidden until the hook beat has played.
            {
                kind: 'text',
                id: 'hook-strap',
                text: {
                    source: 'query',
                    query: { kind: 'script-text', role: 'hook' },
                    required: true,
                },
                style: 'heading',
                styleOverride: {
                    fontSize: 34,
                    fontWeight: 800,
                    textTransform: 'none',
                    letterSpacing: 0,
                    color: '#FFFFFF',
                    textShadow: '0 3px 20px rgba(0,0,0,0.6)',
                    strokeWidth: 0,
                },
                animation: {
                    entrance: 'none',
                    entranceDelayFrames: SEGMENT_FRAMES,
                },
                placement: { anchor: 'top', x: 0.5, y: 0.07, width: 0.84 },
                duration: { kind: 'fill' },
            },
            // The receipt. Rows appear one per 4-beat window and PERSIST (v1 stacks
            // them with `Sequence from={(i + 1) * per}` and never removes them), so
            // `reveal: 'accumulate'`. A fixed length + the sequential overlay
            // accumulator lands its start on frame SEGMENT_FRAMES — v1's first row at
            // `1 * per`.
            {
                kind: 'staggered-list',
                id: 'receipt',
                duration: { kind: 'fixed', frames: RECEIPT_FRAMES },
                items: {
                    texts: {
                        source: 'query',
                        query: { kind: 'script-text', role: 'body' },
                        required: true,
                    },
                    style: 'heading',
                    styleOverride: {
                        fontSize: 42,
                        fontWeight: 700,
                        textTransform: 'none',
                        letterSpacing: 0,
                        color: '#FFFFFF',
                        strokeWidth: 0,
                    },
                    container: 'plate',
                    // v1: fade + 24px rise per row.
                    entrance: 'slide-up',
                },
                // One row per clip window (def beatsPerEdit: 4) — a row lands on each
                // cut, which is what makes it read as a till printing.
                stagger: { beatsPerItem: 4 },
                reveal: 'accumulate',
                align: 'top',
                // v1: rows begin at paddingTop 38%, width 84%.
                placement: { anchor: 'top', x: 0.5, y: 0.38, width: 0.84 },
            },
            // The total stamp, pinned to the last segment (v1 renders it only from
            // `totalFrom = (1 + items.length) * per`). `from-end` keeps it there
            // regardless of how many rows the script produced.
            {
                kind: 'info-card',
                id: 'total-card',
                layout: 'centered',
                // v1's small uppercase "Total" kicker above the number.
                headline: {
                    text: { source: 'fixed', value: 'Total' },
                    style: 'caption',
                },
                // The number itself. The renderer fills this pill with the theme accent
                // and picks a contrasting label colour — v1's accent stamp.
                cta: {
                    text: {
                        source: 'query',
                        query: { kind: 'script-text', role: 'cta' },
                        required: true,
                    },
                    style: 'display',
                },
                // MUST stay transparent: the renderer paints an info-card background
                // across the whole frame (AbsoluteFill), not just the placement box.
                // Transparent also tells it to keep the compile-baked type colours
                // rather than deriving light/dark contrast from a card surface.
                background: { kind: 'solid', color: 'transparent' },
                entrance: { animation: 'pop', entranceDurationFrames: STAMP_FRAMES },
                duration: { kind: 'fixed', frames: SEGMENT_FRAMES },
                start: 'from-end',
                // v1: the stack is bottom-padded 14%; width pulled in to approximate
                // the pill hugging the number.
                placement: { anchor: 'bottom', x: 0.5, y: 0.87, width: 0.62 },
            },
            // The optional worth-comparison line under the total (v1 `valueLine`,
            // 36px/700 cream). Optional exactly like v1, which renders it only when
            // the planner supplied one.
            {
                kind: 'text',
                id: 'value-line',
                text: {
                    source: 'query',
                    query: { kind: 'script-text', role: 'disclaimer' },
                    required: false,
                },
                style: 'heading',
                styleOverride: {
                    fontSize: 36,
                    fontWeight: 700,
                    textTransform: 'none',
                    letterSpacing: 0,
                    // REEL_PALETTE.cream — v1's "clinic voice" warm cream.
                    color: '#EFE0CE',
                    textShadow: '0 3px 16px rgba(0,0,0,0.55)',
                    strokeWidth: 0,
                },
                animation: { entrance: 'pop', entranceDurationFrames: STAMP_FRAMES },
                duration: { kind: 'fixed', frames: SEGMENT_FRAMES },
                start: 'from-end',
                placement: { anchor: 'bottom', x: 0.5, y: 0.945, width: 0.84 },
            },
        ],
    },
    globals: {
        audio: {
            // Music only — no narration (v1 narrationMode: 'text_only',
            // supportsAiVoiceover: false).
            music: { source: 'query', query: { kind: 'music' }, required: false },
        },
        // No captions: the receipt and the total carry the message.
    },
};
//# sourceMappingURL=price-reveal-1.js.map